/**
 * The full column pass, phase 4 (`ColumnBindPasses.full`): verify every
 * visible cell is at its planned height (and heal one rebind per row when
 * not), and the measurement-session column-shape breadcrumb. Moved verbatim
 * out of `fullPass` (r5 FS 1).
 */
import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";
import type { CellDirective, CommitBundle, CommitStructureOp } from "./commit-bundle.ts";
import type { CellRefereeVerdict, RefereeVerdict } from "./commit-referee.ts";
import type { ColumnBindPort, ColumnPassInput } from "./column-bind-port.ts";
import type { FullPassReconciled } from "./full-pass-reconcile.ts";

/**
 * Phase 4a: the post-pass height invariant. Returns `false` when the apply
 * context went stale mid-check (the pass stops there), else `true`.
 */
export function verifyPlannedHeights(port: ColumnBindPort, input: ColumnPassInput, reconciled: FullPassReconciled): boolean {
  const { sheet, defaultColumn, $cells, rows, widthPx, now, applyContext } = input;
  const { plans, bindings } = reconciled;
  // INV(post-pass heights): after a full column pass — skips, directives and
  // rebinds all applied — every visible cell MUST display its plan's height.
  // This is the output-side guarantee the skip gates promise input-side: if
  // any future gate/directive change reintroduces a height-blind keep (the
  // 2026-08-06 pinned-zero class), this catches it the SAME pass instead of
  // never. Field compare ($cell.height vs plan), so animations don't matter.
  // Loud in the console, then best-effort self-heal: evict the row's cursor
  // record and run the full rebind inline — bounded to one repair per row
  // per pass, so a systemic failure stays loud every pass but never loops.
  {
    const mismatched: number[] = [];
    for (let i = 0; i < plans.length; i++) {
      if (!port.applyCurrent(applyContext)) return false;
      const plan = plans[i];
      const $cell = $cells[i];
      if (plan?.layout == null || $cell == null) continue;
      if ($cell.hasAttribute("hidden")) continue;
      if ($cell.style.display === "none") continue;
      if ($cell.height === plan.layout.cellHeightPx) continue;
      mismatched.push(i);
    }
    if (mismatched.length > 0) {
      port.liveScene.invalidate(defaultColumn, mismatched.flatMap((slot) => rows[slot] == null ? [] : [nonnull(rows[slot], "height repair row").id]));
      Tracing.err("ui")(
        `render/verify-heights: ${mismatched.length} visible cell(s) not at plan height after bind pass ` +
          `(column=${defaultColumn.offset} rows=${mismatched
            .slice(0, 5)
            .map((i) => rows[i]?.id.toString() ?? `#${i}`)
            .join(",")}) — healing`,
      );
      Tracing.log("render/verify-heights", {
        column: defaultColumn.offset,
        count: mismatched.length,
        rows: mismatched.slice(0, 10).map((i) => ({
          rid: rows[i]?.id.toString() ?? `#${i}`,
          planH: plans[i]?.layout?.cellHeightPx ?? -1,
          cellH: $cells[i]?.height ?? -1,
        })),
      });
      for (const i of mismatched) {
        const row = nonnull(rows[i], "verify-heights: rows[i]");
        port.renderCursor()?.evict(row.id);
        port.applyPlanFullRebind(
          nonnull($cells[i], "verify-heights: $cells[i]"),
          nonnull(plans[i], "verify-heights: plans[i]"),
          row,
          sheet,
          defaultColumn,
          widthPx,
          now,
          nonnull(plans[i], "height repair semantic index").rowIndex,
          bindings[i] ?? null,
          applyContext,
        );
      }
    }
  }

  return true;
}

/** What the column-shape breadcrumb reports of the pass's decisions. */
export type FullPassShape = {
  /** The pass's commit bundle. */
  readonly bundle: Option<CommitBundle>;
  /** The pass's per-cell directives (`null`: no built scene). */
  readonly directives: Option<readonly CellDirective[]>;
  /** Whether the pass used the built scene. */
  readonly sceneUsedThisPass: boolean;
  /** The commit referee's verdict. */
  readonly commitVerdict: Option<RefereeVerdict>;
  /** The cell referee's verdict. */
  readonly cellVerdict: Option<CellRefereeVerdict>;
};

/** Phase 4b: the `render-next/column-shape` breadcrumb (diagnostics only). */
export function logColumnShape(
  port: ColumnBindPort,
  input: ColumnPassInput,
  reconciled: FullPassReconciled,
  shape: FullPassShape,
): void {
  const { defaultColumn, $cells, rows } = input;
  const { oldRowIds } = reconciled;
  const { bundle, directives, sceneUsedThisPass, commitVerdict, cellVerdict } = shape;
  // TEMP diagnostic (measurement sessions only): one aggregate breadcrumb per
  // column commit describing the shape of what this bind produced — target vs
  // final cell counts, visible/hidden split, structure/directive tallies, and
  // the LR6 + referee verdicts. `anomaly` flags a column that lost cells or a
  // loud referee — the "columns disappearing" signature.
  // Bounded, JSON-safe aggregates only — never row data. Pure observability.
  if (port.renderFlags().diagnostics()) {
    const targetRows = rows.length;
    const finalCells = $cells.length;
    let visible = 0;
    let hidden = 0;
    for (const $c of $cells) {
      if ($c.hasAttribute("hidden") || $c.style.display === "none") hidden += 1;
      else visible += 1;
    }
    const structure = summarizeStructureOps(bundle?.structure ?? []);
    const directiveCounts =
      directives != null ? summarizeDirectiveTags(directives) : null;
    const anomaly =
      finalCells !== targetRows ||
      commitVerdict === "loud" ||
      cellVerdict === "loud";
    Tracing.log("render-next/column-shape", {
      column: defaultColumn.offset,
      targetRows,
      poolCells: oldRowIds.length,
      finalCells,
      visible,
      hidden,
      sceneUsed: sceneUsedThisPass,
      directivesHonored: port.renderFlags().live(),
      structure,
      directives: directiveCounts,
      lr6: port.renderCursor()?.snapshot() ?? null,
      commitVerdict,
      cellVerdict,
      anomaly,
    });
  }
}

/**
 * TEMP diagnostic helper — bounded tally of a commit bundle's structure ops by
 * tag, for the `render-next/column-shape` breadcrumb. Pure / JSON-safe.
 */
function summarizeStructureOps(
  ops: readonly CommitStructureOp[],
): { keep: number; mount: number; recycle: number; remove: number } {
  const s = { keep: 0, mount: 0, recycle: 0, remove: 0 };
  for (const op of ops) s[op.tag] += 1;
  return s;
}

/**
 * TEMP diagnostic helper — bounded tally of the per-cell directives by tag, for
 * the `render-next/column-shape` breadcrumb. Pure / JSON-safe.
 */
function summarizeDirectiveTags(
  ds: readonly CellDirective[],
): { rebind: number; skip: number; resize: number; span: number } {
  const s = { rebind: 0, skip: 0, resize: 0, span: 0 };
  for (const d of ds) s[d.tag] += 1;
  return s;
}

